import { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import categories from '~/packages/categories'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMedia } from 'react-use'
import MenuItem from '~/components/menuItem'

interface CategoryListToggleProps {
  categoryKey: string
  categoryName: string
  selectedCategory: typeof categories
  categoriesInfo: {
    key: string
    title: string
    sectorKey: string
  }[]
  handleSelectCategory: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    info: {
      key: string
      title: string
      sectorKey: string
    },
  ) => void
}

const TitleContainer = styled.div`
  display: flex;
  gap: 4px;
  place-items: center;
  cursor: pointer;
  transition-duration: 0.3s;

  h5 {
    font-weight: 500;
    font-size: 0.7rem;
    text-transform: uppercase;
    margin: 1rem 0;
    color: ${({ theme }) => theme.colors.darkGrey};
    user-select: none;
    letter-spacing: 1px;
  }

  svg {
    fill: ${({ theme }) => theme.colors.darkGrey};
    transition: transform 0.2s ease-in-out;
  }
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  margin: 0 2px;
  overflow: hidden;
`

export function CategoryListToggle({
  categoryKey,
  categoryName,
  selectedCategory,
  categoriesInfo,
  handleSelectCategory,
}: CategoryListToggleProps) {
  const isMobile = useMedia('(max-width: 768px)')
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(isMobile)

  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (sectionRef.current) {
      sectionRef.current.style.maxHeight = isCategoryMenuOpen
        ? `${sectionRef.current.scrollHeight}px`
        : '0'
    }
  }, [isCategoryMenuOpen])

  const toggleCategoryMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    event.preventDefault()
    setIsCategoryMenuOpen(prev => !prev)
    sectionRef!.current!.style.transitionDuration = '0.3s'
  }

  return (
    <>
      <TitleContainer onClick={toggleCategoryMenu}>
        <h5>{categoryName}</h5>
        {isCategoryMenuOpen ? (
          <FontAwesomeIcon icon={faChevronUp} width={10} height={10} />
        ) : (
          <FontAwesomeIcon icon={faChevronDown} width={10} height={10} />
        )}
      </TitleContainer>
      <SectionContainer ref={sectionRef}>
        {categoriesInfo
          .filter(info => info.sectorKey === categoryKey)
          .map(info => (
            <MenuItem
              key={`button-${info.key}`}
              $isActive={Object.keys(selectedCategory)[0] === info.key}
              onClick={event => handleSelectCategory(event, info)}
              label={info.title}
              size='xs'
            />
          ))}
      </SectionContainer>
    </>
  )
}
